/**
 * "Review PRs with Proctor": the one-click review Team, as logic.
 *
 * The Team this makes has Proctor on it and nobody else, one review line
 * switched on, and the picked repositories attached. Engaging Proctor claims
 * every attached repository no other Team already reviews (first claim
 * stands), so the Team reviews from the moment it exists. The create rides
 * the explicit `name` + `goal` + `sources` path: no prompt, so no model call
 * drafts the Brief and the create is fast and free.
 *
 * Three reads answer every question the picker has to ask first:
 *
 *   - `GET /api/connected-services`: whether the WORKSPACE has GitHub.
 *   - `GET /api/repos`: the repositories added to the workspace. Only these
 *     route pull-request webhooks, so offering the installation's other
 *     repositories would make a Team that never reviews anything.
 *   - `GET /api/code-review/repo-claims`: which of them another Team already
 *     reviews, and which Team, so those rows show as taken.
 */
import type { CreateTeamLineInput, RepoClaimsResponse, ReviewReposSurface } from "@yagni/shared";
import { postTeam, type StartRepo } from "./startMyTeamRequest.js";

/** Another Team that already reviews a repository. */
export interface ReviewHolder {
  /** Null, like the name, when the reader cannot see that Team. */
  deskId: string | null;
  deskName: string | null;
}

export interface ReviewRepo extends StartRepo {
  /** The Team that already reviews it, or null when it is free. */
  heldBy: ReviewHolder | null;
}

export interface ReviewState {
  /** The workspace has GitHub connected. */
  github: boolean;
  repos: ReviewRepo[];
  /**
   * The claims read answered. When it did not, every repository reads free
   * and the picker says it could not check.
   */
  claimsKnown: boolean;
}

type ServiceRow = { serviceType?: unknown; connectionScope?: unknown };
type RepoRow = { id?: unknown; owner?: unknown; name?: unknown };

/** Read GitHub, the workspace's repositories, and who reviews which. */
export async function readReviewState(fetchImpl: typeof fetch = fetch): Promise<ReviewState> {
  const [servicesRes, reposRes, claimsRes] = await Promise.all([
    fetchImpl("/api/connected-services", { credentials: "include" }),
    fetchImpl("/api/repos", { credentials: "include" }),
    // A failed claims read is not a dead end: the create reports a held repo
    // afterwards anyway, so the picker degrades to "nothing looks taken" and
    // says it could not check (`claimsKnown`).
    fetchImpl("/api/code-review/repo-claims", { credentials: "include" }).catch((err: unknown) => {
      console.warn("review PRs: the repo-claims read failed", err);
      return null;
    }),
  ]);
  if (!servicesRes.ok) throw new Error(`connected-services ${servicesRes.status}`);
  if (!reposRes.ok) throw new Error(`repos ${reposRes.status}`);
  const services = (await servicesRes.json()) as unknown;
  const repoRows = (await reposRes.json()) as unknown;
  if (claimsRes && !claimsRes.ok) {
    console.warn(`review PRs: the repo-claims read answered ${claimsRes.status}`);
  }
  const claimsBody =
    claimsRes && claimsRes.ok
      ? ((await claimsRes.json().catch((err: unknown) => {
          console.warn("review PRs: the repo-claims answer did not parse", err);
          return null;
        })) as RepoClaimsResponse | null)
      : null;
  if (claimsBody && !Array.isArray(claimsBody.claims)) {
    console.warn("review PRs: the repo-claims answer carried no claims list");
  }
  const claims = claimsBody?.claims;
  const claimsKnown = Array.isArray(claims);

  const rows: ServiceRow[] = Array.isArray(services) ? (services as ServiceRow[]) : [];
  // GitHub is workspace-wide; an absent scope is a workspace row.
  const github = rows.some(
    (row) => row.serviceType === "github" && row.connectionScope !== "personal",
  );

  const holders = new Map<string, ReviewHolder>();
  for (const claim of Array.isArray(claims) ? claims : []) {
    if (typeof claim?.repo === "string") {
      holders.set(claim.repo.toLowerCase(), {
        deskId: typeof claim.deskId === "string" ? claim.deskId : null,
        deskName: typeof claim.deskName === "string" ? claim.deskName : null,
      });
    }
  }

  const repos: ReviewRepo[] = [];
  if (Array.isArray(repoRows)) {
    for (const row of repoRows as RepoRow[]) {
      if (
        typeof row?.id === "string" &&
        typeof row.owner === "string" &&
        typeof row.name === "string"
      ) {
        const ref = `${row.owner}/${row.name}`;
        repos.push({ id: row.id, ref, heldBy: holders.get(ref.toLowerCase()) ?? null });
      }
    }
  }
  return { github, repos, claimsKnown };
}

/** How many repositories the goal sentence names before it counts the rest. */
const GOAL_NAMES_MAX = 3;

/** `a`, `a and b`, `a, b and c`, `a, b, c and 2 more repositories`. */
export function listRepos(refs: readonly string[]): string {
  if (refs.length <= 1) return refs[0] ?? "";
  if (refs.length <= GOAL_NAMES_MAX) {
    return `${refs.slice(0, -1).join(", ")} and ${refs[refs.length - 1]}`;
  }
  const rest = refs.length - GOAL_NAMES_MAX;
  return `${refs.slice(0, GOAL_NAMES_MAX).join(", ")} and ${rest} more ${
    rest === 1 ? "repository" : "repositories"
  }`;
}

/**
 * The Team's name: `api reviews` for one repository, `PR reviews` for more.
 * Team names are not unique (there is no index on them), so a second review
 * Team on the same repository is simply another Team with the same name.
 */
export function reviewTeamName(refs: readonly string[]): string {
  if (refs.length === 1) {
    const name = refs[0]!.split("/").pop() || refs[0]!;
    return `${name} reviews`;
  }
  return "PR reviews";
}

export interface ReviewTeamBody {
  name: string;
  goal: string;
  sources: ["github"];
  attachments: Array<{ kind: "repo"; source: "github"; ref: string }>;
  workers: ["proctor"];
  lines: CreateTeamLineInput[];
}

/**
 * The create request. The review line carries no `filter.repos`: an empty
 * list means every repository attached to the Team, so attaching another one
 * later needs nothing else. It starts at the catalog's rung (Supervised).
 */
export function buildReviewTeamBody(refs: readonly string[]): ReviewTeamBody {
  return {
    name: reviewTeamName(refs),
    goal: `Review every pull request on ${listRepos(refs)}.`,
    sources: ["github"],
    attachments: refs.map((ref) => ({ kind: "repo", source: "github", ref })),
    workers: ["proctor"],
    lines: [{ kind: "reviewer.review_pr", active: true }],
  };
}

export type ReviewTeamResult =
  | {
      ok: true;
      id: string;
      /** Proctor got onto the Team and the review line is on. */
      proctorOn: boolean;
      /** The server's own reason, when Proctor or the line did not land. */
      problem: string | null;
      /** Picked repositories the Team could not attach, with the reason. */
      attachmentsFailed: Array<{ ref: string; error: string }>;
    }
  | { ok: false; message: string };

interface SetupPayload {
  setup?: {
    workers?: { engaged?: unknown; failed?: Array<{ error?: unknown }> };
    lines?: { failed?: Array<{ error?: unknown }> };
    attachmentsFailed?: Array<{ ref?: unknown; error?: unknown }>;
  };
}

/** POST the review Team and read what its setup did. */
export async function startReviewTeam(
  refs: readonly string[],
  fetchImpl: typeof fetch = fetch,
): Promise<ReviewTeamResult> {
  const result = await postTeam(buildReviewTeamBody(refs), fetchImpl);
  if (!result.ok) return result;
  const setup = (result.payload as SetupPayload | null)?.setup;
  const engaged = Array.isArray(setup?.workers?.engaged) ? setup.workers.engaged : [];
  const lineFailures = setup?.lines?.failed ?? [];
  const proctorOn = engaged.includes("proctor") && lineFailures.length === 0;
  const reason = [...(setup?.workers?.failed ?? []), ...lineFailures]
    .map((failure) => failure?.error)
    .find((error): error is string => typeof error === "string" && error.length > 0);
  const attachmentsFailed = (
    Array.isArray(setup?.attachmentsFailed) ? setup.attachmentsFailed : []
  ).flatMap((failure) =>
    typeof failure?.ref === "string"
      ? [{ ref: failure.ref, error: typeof failure.error === "string" ? failure.error : "" }]
      : [],
  );
  return {
    ok: true,
    id: result.id,
    proctorOn,
    problem: proctorOn ? null : (reason ?? null),
    attachmentsFailed,
  };
}

/** A repository on the new Team that another Team reviews after all. */
export interface HeldRepo {
  ref: string;
  deskName: string | null;
}

/**
 * The race the picker cannot see: another Team claiming a repository between
 * the read and the create. The new Team's own review surface says so. A failed
 * read reports nothing held, never an error: the Team exists either way.
 */
export async function readHeldAfterCreate(
  deskId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<HeldRepo[]> {
  try {
    const res = await fetchImpl(
      `/api/code-review/desks/${encodeURIComponent(deskId)}/review-repos`,
      { credentials: "include" },
    );
    if (!res.ok) {
      console.warn(`review PRs: the held-repo check for Team ${deskId} answered ${res.status}`);
      return [];
    }
    const surface = (await res.json()) as Partial<ReviewReposSurface> | null;
    const rows = Array.isArray(surface?.repos) ? surface.repos : [];
    return rows
      .filter((row) => row?.status === "held_elsewhere")
      .map((row) => ({ ref: row.ref, deskName: row.claimingDeskName ?? null }));
  } catch (err) {
    console.warn(`review PRs: the held-repo check for Team ${deskId} failed`, err);
    return [];
  }
}

/**
 * What the flow has to say about a Team it just made, one line each: a
 * repository that did not attach, Proctor not landing, or a repository
 * another Team claimed in the meantime. Empty means it all went through, and
 * the flow opens the Team.
 */
export async function reviewCreateNotes(
  result: Extract<ReviewTeamResult, { ok: true }>,
  words: {
    attachFailed: (ref: string, error: string) => string;
    notEngaged: string;
    heldAfter: (ref: string, deskName: string | null) => string;
  },
  fetchImpl: typeof fetch = fetch,
): Promise<string[]> {
  const lines = result.attachmentsFailed.map((failure) =>
    words.attachFailed(failure.ref, failure.error),
  );
  if (!result.proctorOn) {
    lines.push(result.problem ? `${words.notEngaged} ${result.problem}` : words.notEngaged);
    return lines;
  }
  for (const held of await readHeldAfterCreate(result.id, fetchImpl)) {
    lines.push(words.heldAfter(held.ref, held.deskName));
  }
  return lines;
}
